import type * as d3 from "d3";

export interface DummyNode extends d3.SimulationNodeDatum {
  id: string;
  label: string;
  type: string;
  group: string;
  size: number;
  description?: string;
}

export interface DummyLink extends d3.SimulationLinkDatum<DummyNode> {
  source: string | DummyNode;
  target: string | DummyNode;
  relationship: string;
}

export const memoryNodes: DummyNode[] = [
  {
    id: "user",
    label: "Me (Alex)",
    type: "user",
    group: "user",
    size: 30,
    description: "A 28-year-old software engineer living in San Francisco.",
  },

  // ## Personal Information
  {
    id: "name",
    label: "Alex Chen",
    type: "identity",
    group: "personal",
    size: 20,
  },
  {
    id: "birthday",
    label: "March 15, 1996",
    type: "date",
    group: "personal",
    size: 20,
  },
  {
    id: "age",
    label: "28 years old",
    type: "number",
    group: "personal",
    size: 20,
  },
  {
    id: "hometown",
    label: "Austin, Texas",
    type: "location",
    group: "personal",
    size: 20,
  },
  {
    id: "current_city",
    label: "San Francisco",
    type: "location",
    group: "personal",
    size: 20,
  },
  {
    id: "email",
    label: "alex.chen@email.com",
    type: "contact",
    group: "personal",
    size: 20,
  },
  {
    id: "phone",
    label: "555-0101",
    type: "contact",
    group: "personal",
    size: 20,
  },
  {
    id: "personality_type",
    label: "INTJ",
    type: "trait",
    group: "personal",
    size: 20,
    description: "MBTI Personality Type",
  },

  // ## Pet
  {
    id: "pet_buster",
    label: "Buster",
    type: "pet",
    group: "family",
    size: 22,
    description: "His 2-year-old Golden Retriever",
  },
  {
    id: "pet_breed",
    label: "Golden Retriever",
    type: "breed",
    group: "family",
    size: 16,
  },
  {
    id: "pet_activity_walks",
    label: "Morning Walks with Buster",
    type: "routine",
    group: "family",
    size: 18,
  },
  {
    id: "pet_park",
    label: "Fort Funston Dog Park",
    type: "place",
    group: "family",
    size: 16,
  },
  {
    id: "pet_vet",
    label: "SF Pet Hospital",
    type: "service",
    group: "family",
    size: 16,
  },

  // ## Work & Career
  {
    id: "job_title",
    label: "Senior Software Engineer",
    type: "work",
    group: "work",
    size: 22,
  },
  { id: "company", label: "Google", type: "work", group: "work", size: 22 },
  {
    id: "work_schedule",
    label: "9 AM - 6 PM Hybrid",
    type: "schedule",
    group: "work",
    size: 20,
  },
  {
    id: "salary",
    label: "$180,000/year",
    type: "finance",
    group: "work",
    size: 20,
  },
  {
    id: "boss",
    label: "Sarah Johnson",
    type: "person",
    group: "work",
    size: 20,
    description: "Engineering Manager",
  },
  {
    id: "coworkers",
    label: "Core Search Team",
    type: "group",
    group: "work",
    size: 20,
  },
  {
    id: "work_projects",
    label: "Search Algorithm Optimization",
    type: "task",
    group: "work",
    size: 20,
  },
  {
    id: "project_kronos",
    label: "Project Kronos",
    type: "task",
    group: "work",
    size: 18,
    description: "Codename for current project",
  },
  {
    id: "kronos_goal_latency",
    label: "Reduce p99 Latency by 5%",
    type: "goal",
    group: "work",
    size: 16,
  },
  {
    id: "tool_jira",
    label: "JIRA",
    type: "app",
    group: "work",
    size: 16,
    description: "For task tracking",
  },
  {
    id: "career_goals",
    label: "Become Tech Lead",
    type: "goal",
    group: "work",
    size: 20,
  },
  {
    id: "work_skills_main",
    label: "Backend Systems",
    type: "skill",
    group: "work",
    size: 18,
  },
  {
    id: "work_skills_tech",
    label: "Python, C++, GCP",
    type: "skill",
    group: "work",
    size: 18,
  },
  {
    id: "github",
    label: "GitHub Profile",
    type: "link",
    group: "work",
    size: 18,
    description: "github.com/alexchen",
  },
  {
    id: "linkedin",
    label: "LinkedIn Profile",
    type: "link",
    group: "work",
    size: 18,
  },
  {
    id: "work_deadlines",
    label: "Q4 Project Deadline",
    type: "date",
    group: "work",
    size: 18,
  },

  // ## Meetings & Assignments
  {
    id: "meeting_weekly_sync",
    label: "Weekly Team Sync",
    type: "event",
    group: "work",
    size: 16,
    description: "Mondays at 10 AM",
  },
  {
    id: "meeting_one_on_one",
    label: "1:1 with Sarah",
    type: "event",
    group: "work",
    size: 16,
    description: "Bi-weekly on Wednesdays",
  },
  {
    id: "meeting_project_alpha",
    label: "Project Kronos Standup",
    type: "event",
    group: "work",
    size: 16,
    description: "Daily at 9:30 AM",
  },
  {
    id: "assignment_code_review",
    label: "Peer Code Review",
    type: "task",
    group: "work",
    size: 16,
  },
  {
    id: "assignment_design_doc",
    label: "New Feature Design Doc",
    type: "task",
    group: "work",
    size: 16,
  },

  // ## Relationships & Social
  {
    id: "girlfriend",
    label: "Emma Rodriguez",
    type: "person",
    group: "family",
    size: 24,
    description: "Dating for 2 years",
  },
  {
    id: "emma_job",
    label: "Graphic Designer",
    type: "work",
    group: "family",
    size: 18,
  },
  {
    id: "emma_birthday",
    label: "July 22",
    type: "date",
    group: "family",
    size: 18,
  },
  {
    id: "anniversary",
    label: "Anniversary: June 5th",
    type: "date",
    group: "family",
    size: 18,
  },
  {
    id: "parents",
    label: "David & Linda Chen",
    type: "family",
    group: "family",
    size: 20,
  },
  {
    id: "siblings",
    label: "Michael Chen",
    type: "family",
    group: "family",
    size: 20,
    description: "Younger brother",
  },
  {
    id: "best_friend",
    label: "Jake Martinez",
    type: "person",
    group: "social",
    size: 20,
  },
  {
    id: "close_friends",
    label: "College Squad",
    type: "group",
    group: "social",
    size: 20,
  },
  {
    id: "friend_maria",
    label: "Maria Garcia",
    type: "person",
    group: "social",
    size: 18,
    description: "Friend from college, now a doctor",
  },
  {
    id: "roommate",
    label: "Kevin Park",
    type: "person",
    group: "social",
    size: 20,
  },
  {
    id: "shared_activity_hiking",
    label: "Hiking with Emma",
    type: "activity",
    group: "social",
    size: 16,
  },
  {
    id: "social_media",
    label: "Instagram & X",
    type: "app",
    group: "social",
    size: 16,
  },
  {
    id: "event_warriors_game",
    label: "Warriors Game",
    type: "event",
    group: "social",
    size: 18,
    description: "Upcoming game with Jake",
  },

  // ## Health, Fitness & Gym
  {
    id: "gym_membership",
    label: "Equinox SOMA",
    type: "subscription",
    group: "health",
    size: 22,
  },
  {
    id: "workout_routine",
    label: "Push/Pull/Legs Split",
    type: "routine",
    group: "health",
    size: 20,
  },
  {
    id: "fitness_goals",
    label: "Bench 225 lbs",
    type: "goal",
    group: "health",
    size: 20,
  },
  {
    id: "doctor",
    label: "Dr. Patricia Kim",
    type: "person",
    group: "health",
    size: 20,
  },
  {
    id: "medications",
    label: "Daily Multivitamin",
    type: "health",
    group: "health",
    size: 20,
  },
  {
    id: "allergies",
    label: "Shellfish, Peanuts",
    type: "health",
    group: "health",
    size: 20,
  },
  {
    id: "sleep_schedule",
    label: "11 PM - 7 AM",
    type: "routine",
    group: "health",
    size: 20,
  },
  {
    id: "exercise_bench",
    label: "Bench Press",
    type: "activity",
    group: "health",
    size: 16,
  },
  {
    id: "exercise_squat",
    label: "Squat",
    type: "activity",
    group: "health",
    size: 16,
  },
  {
    id: "exercise_deadlift",
    label: "Deadlift",
    type: "activity",
    group: "health",
    size: 16,
  },
  {
    id: "pr_bench",
    label: "Bench PR: 215 lbs",
    type: "milestone",
    group: "health",
    size: 16,
  },
  {
    id: "emergency_contact",
    label: "Emma Rodriguez",
    type: "person",
    group: "health",
    size: 18,
  },
  {
    id: "insurance",
    label: "BlueCross BlueShield",
    type: "service",
    group: "health",
    size: 18,
  },
  { id: "blood_type", label: "O+", type: "health", group: "health", size: 18 },

  // ## Food Preferences & Dining
  {
    id: "favorite_restaurant",
    label: "State Bird Provisions",
    type: "place",
    group: "food",
    size: 22,
  },
  {
    id: "dietary_restrictions",
    label: "Pescatarian",
    type: "health",
    group: "food",
    size: 20,
  },
  {
    id: "favorite_cuisine",
    label: "Japanese",
    type: "preference",
    group: "food",
    size: 20,
  },
  {
    id: "coffee_order",
    label: "Oat Milk Latte",
    type: "preference",
    group: "food",
    size: 20,
  },
  {
    id: "grocery_store",
    label: "Whole Foods",
    type: "place",
    group: "food",
    size: 20,
  },
  {
    id: "cooking_skills",
    label: "Intermediate Cook",
    type: "skill",
    group: "food",
    size: 20,
  },
  {
    id: "favorite_dish_sushi",
    label: "Spicy Tuna Roll",
    type: "preference",
    group: "food",
    size: 16,
  },
  {
    id: "favorite_dish_ramen",
    label: "Tonkotsu Ramen",
    type: "preference",
    group: "food",
    size: 16,
  },
  {
    id: "disliked_food",
    label: "Cilantro",
    type: "preference",
    group: "food",
    size: 16,
  },
  {
    id: "favorite_drink",
    label: "Iced Green Tea",
    type: "preference",
    group: "food",
    size: 16,
  },

  // ## Habits & Routines
  {
    id: "morning_routine",
    label: "Walk Dog, Coffee, Gym",
    type: "routine",
    group: "habits",
    size: 22,
  },
  {
    id: "evening_routine",
    label: "Dinner, Read, Netflix",
    type: "routine",
    group: "habits",
    size: 22,
  },
  {
    id: "weekend_habits",
    label: "Hike, Dog Park, Chores",
    type: "routine",
    group: "habits",
    size: 20,
  },
  {
    id: "bad_habits",
    label: "Procrastination",
    type: "habit",
    group: "habits",
    size: 18,
  },
  {
    id: "good_habits",
    label: "Daily Exercise",
    type: "habit",
    group: "habits",
    size: 18,
  },
  {
    id: "habit_meditation",
    label: "10-min Meditation",
    type: "habit",
    group: "habits",
    size: 16,
    description: "Uses Headspace app",
  },
  {
    id: "habit_journaling",
    label: "Daily Journaling",
    type: "habit",
    group: "habits",
    size: 16,
    description: "Uses Notion",
  },

  // ## Entertainment & Hobbies
  {
    id: "hobbies",
    label: "Photography",
    type: "activity",
    group: "entertainment",
    size: 22,
  },
  {
    id: "camera_sony_a7iv",
    label: "Sony α7 IV",
    type: "device",
    group: "entertainment",
    size: 16,
  },
  {
    id: "photo_style_landscape",
    label: "Landscape Photography",
    type: "interest",
    group: "entertainment",
    size: 16,
  },
  {
    id: "photo_project_yosemite",
    label: "Yosemite Trip Photos",
    type: "event",
    group: "entertainment",
    size: 16,
  },
  {
    id: "favorite_movie",
    label: "Inception",
    type: "media",
    group: "entertainment",
    size: 20,
  },
  {
    id: "favorite_tv_show",
    label: "The Office",
    type: "media",
    group: "entertainment",
    size: 20,
  },
  {
    id: "music_taste",
    label: "Indie Rock",
    type: "preference",
    group: "entertainment",
    size: 20,
  },
  {
    id: "video_games",
    label: "League of Legends",
    type: "activity",
    group: "entertainment",
    size: 20,
  },
  {
    id: "books",
    label: "Sci-Fi Novels",
    type: "media",
    group: "entertainment",
    size: 20,
  },
  {
    id: "sports_teams",
    label: "Golden State Warriors",
    type: "interest",
    group: "entertainment",
    size: 20,
  },
  {
    id: "favorite_apps",
    label: "Spotify & Notion",
    type: "app",
    group: "entertainment",
    size: 18,
  },
  {
    id: "streaming_services",
    label: "Netflix & Spotify",
    type: "subscription",
    group: "entertainment",
    size: 18,
  },
  {
    id: "podcast",
    label: "Lex Fridman Podcast",
    type: "media",
    group: "entertainment",
    size: 16,
  },
  {
    id: "tech_blog_hacker_news",
    label: "Hacker News",
    type: "media",
    group: "entertainment",
    size: 16,
    description: "Daily tech reading",
  },

  // ## Education & Learning
  {
    id: "college",
    label: "Stanford University",
    type: "education",
    group: "learning",
    size: 22,
  },
  {
    id: "degree",
    label: "BS in Computer Science",
    type: "education",
    group: "learning",
    size: 20,
  },
  {
    id: "current_courses",
    label: "ML on Coursera",
    type: "education",
    group: "learning",
    size: 20,
  },
  {
    id: "certifications",
    label: "AWS Certified",
    type: "education",
    group: "learning",
    size: 20,
  },
  {
    id: "learning_goals",
    label: "Advanced System Design",
    type: "goal",
    group: "learning",
    size: 20,
  },
  {
    id: "assignment_ml_project",
    label: "Neural Network Project",
    type: "task",
    group: "learning",
    size: 18,
  },
  {
    id: "current_book",
    label: "Reading 'Dune'",
    type: "media",
    group: "learning",
    size: 16,
  },
  {
    id: "author_asimov",
    label: "Isaac Asimov",
    type: "person",
    group: "learning",
    size: 14,
    description: "Favorite author",
  },

  // ## Financial
  {
    id: "bank",
    label: "Chase Bank",
    type: "service",
    group: "finance",
    size: 22,
  },
  {
    id: "credit_score",
    label: "785",
    type: "number",
    group: "finance",
    size: 20,
  },
  {
    id: "credit_card_chase",
    label: "Chase Sapphire Reserve",
    type: "service",
    group: "finance",
    size: 18,
  },
  {
    id: "investments",
    label: "Index Funds & Tech",
    type: "finance",
    group: "finance",
    size: 20,
  },
  {
    id: "budget",
    label: "50/30/20 Rule",
    type: "plan",
    group: "finance",
    size: 20,
  },
  {
    id: "financial_goals",
    label: "Buy a House by 30",
    type: "goal",
    group: "finance",
    size: 20,
  },
  {
    id: "rent_amount",
    label: "$3,500/month rent",
    type: "expense",
    group: "finance",
    size: 18,
  },

  // ## Housing & Transportation
  {
    id: "apartment",
    label: "Mission Bay Apartment",
    type: "place",
    group: "location",
    size: 22,
  },
  {
    id: "car",
    label: "Tesla Model 3",
    type: "vehicle",
    group: "location",
    size: 20,
  },
  {
    id: "commute",
    label: "25 min drive to work",
    type: "routine",
    group: "location",
    size: 20,
  },
  {
    id: "public_transport",
    label: "Uses BART/Muni",
    type: "service",
    group: "location",
    size: 18,
  },
  {
    id: "dream_destination",
    label: "Travel to Japan",
    type: "goal",
    group: "location",
    size: 18,
  },
  {
    id: "last_vacation",
    label: "Hawaii Trip",
    type: "event",
    group: "location",
    size: 18,
    description: "Went with Emma last year",
  },
];

export const memoryLinks: DummyLink[] = [
  // User -> Core Nodes
  { source: "user", target: "name", relationship: "is" },
  { source: "user", target: "age", relationship: "is" },
  { source: "user", target: "current_city", relationship: "lives_in" },
  { source: "user", target: "job_title", relationship: "works_as" },
  { source: "user", target: "girlfriend", relationship: "dating" },
  { source: "user", target: "parents", relationship: "child_of" },
  { source: "user", target: "best_friend", relationship: "friends_with" },
  { source: "user", target: "gym_membership", relationship: "has" },
  { source: "user", target: "hobbies", relationship: "enjoys" },
  { source: "user", target: "financial_goals", relationship: "aspires_to" },
  { source: "user", target: "pet_buster", relationship: "owns" },

  // Personal Info & Pet
  { source: "name", target: "email", relationship: "contact" },
  { source: "name", target: "phone", relationship: "contact" },
  { source: "birthday", target: "age", relationship: "determines" },
  { source: "hometown", target: "user", relationship: "grew_up_in" },
  { source: "pet_buster", target: "pet_breed", relationship: "is_a" },
  { source: "pet_buster", target: "pet_vet", relationship: "patient_of" },
  {
    source: "morning_routine",
    target: "pet_activity_walks",
    relationship: "includes",
  },
  { source: "weekend_habits", target: "pet_park", relationship: "visits" },

  // Work & Career
  { source: "job_title", target: "company", relationship: "at" },
  { source: "company", target: "boss", relationship: "employs" },
  { source: "boss", target: "coworkers", relationship: "manages" },
  { source: "job_title", target: "salary", relationship: "earns" },
  { source: "job_title", target: "work_skills_main", relationship: "requires" },
  {
    source: "work_skills_main",
    target: "work_skills_tech",
    relationship: "includes",
  },
  {
    source: "work_projects",
    target: "project_kronos",
    relationship: "includes",
  },
  {
    source: "project_kronos",
    target: "kronos_goal_latency",
    relationship: "goal_is",
  },
  { source: "project_kronos", target: "tool_jira", relationship: "tracked_in" },
  { source: "work_projects", target: "work_deadlines", relationship: "has" },
  {
    source: "career_goals",
    target: "learning_goals",
    relationship: "requires",
  },
  { source: "user", target: "linkedin", relationship: "profiled_on" },
  { source: "user", target: "github", relationship: "contributes_on" },

  // Meetings & Assignments
  {
    source: "work_schedule",
    target: "meeting_weekly_sync",
    relationship: "includes",
  },
  {
    source: "project_kronos",
    target: "meeting_project_alpha",
    relationship: "has",
  },
  { source: "boss", target: "meeting_one_on_one", relationship: "conducts" },
  {
    source: "work_projects",
    target: "assignment_design_doc",
    relationship: "requires",
  },
  {
    source: "coworkers",
    target: "assignment_code_review",
    relationship: "performs",
  },

  // Relationships
  { source: "girlfriend", target: "emma_job", relationship: "works_as" },
  { source: "girlfriend", target: "anniversary", relationship: "celebrates" },
  {
    source: "girlfriend",
    target: "emma_birthday",
    relationship: "birthday_is",
  },
  {
    source: "girlfriend",
    target: "shared_activity_hiking",
    relationship: "enjoys",
  },
  { source: "user", target: "shared_activity_hiking", relationship: "enjoys" },
  { source: "parents", target: "siblings", relationship: "parent_of" },
  { source: "roommate", target: "apartment", relationship: "shares" },
  { source: "close_friends", target: "friend_maria", relationship: "includes" },
  { source: "best_friend", target: "close_friends", relationship: "part_of" },
  {
    source: "best_friend",
    target: "event_warriors_game",
    relationship: "attending_with",
  },

  // Health & Gym
  {
    source: "gym_membership",
    target: "workout_routine",
    relationship: "enables",
  },
  {
    source: "workout_routine",
    target: "fitness_goals",
    relationship: "supports",
  },
  {
    source: "workout_routine",
    target: "exercise_bench",
    relationship: "includes",
  },
  {
    source: "workout_routine",
    target: "exercise_squat",
    relationship: "includes",
  },
  {
    source: "workout_routine",
    target: "exercise_deadlift",
    relationship: "includes",
  },
  { source: "exercise_bench", target: "pr_bench", relationship: "record" },
  { source: "fitness_goals", target: "pr_bench", relationship: "tracks" },
  { source: "doctor", target: "insurance", relationship: "accepts" },
  {
    source: "allergies",
    target: "dietary_restrictions",
    relationship: "influences",
  },
  { source: "user", target: "blood_type", relationship: "has" },
  { source: "emergency_contact", target: "girlfriend", relationship: "is" },

  // Food & Dining
  {
    source: "favorite_restaurant",
    target: "favorite_cuisine",
    relationship: "serves",
  },
  {
    source: "favorite_cuisine",
    target: "favorite_dish_sushi",
    relationship: "includes",
  },
  {
    source: "favorite_cuisine",
    target: "favorite_dish_ramen",
    relationship: "includes",
  },
  {
    source: "dietary_restrictions",
    target: "allergies",
    relationship: "due_to",
  },
  {
    source: "grocery_store",
    target: "cooking_skills",
    relationship: "supplies_for",
  },
  { source: "user", target: "disliked_food", relationship: "dislikes" },

  // Habits & Routines
  {
    source: "morning_routine",
    target: "coffee_order",
    relationship: "starts_with",
  },
  {
    source: "morning_routine",
    target: "habit_meditation",
    relationship: "includes",
  },
  {
    source: "evening_routine",
    target: "current_book",
    relationship: "includes_reading",
  },
  {
    source: "evening_routine",
    target: "habit_journaling",
    relationship: "includes",
  },
  {
    source: "sleep_schedule",
    target: "morning_routine",
    relationship: "affects",
  },
  {
    source: "weekend_habits",
    target: "shared_activity_hiking",
    relationship: "can_include",
  },

  // Entertainment & Hobbies
  { source: "hobbies", target: "camera_sony_a7iv", relationship: "uses" },
  {
    source: "camera_sony_a7iv",
    target: "photo_style_landscape",
    relationship: "suited_for",
  },
  {
    source: "last_vacation",
    target: "photo_project_yosemite",
    relationship: "inspired",
  },
  {
    source: "photo_style_landscape",
    target: "photo_project_yosemite",
    relationship: "style_of",
  },
  {
    source: "music_taste",
    target: "favorite_apps",
    relationship: "listens_on",
  },
  {
    source: "streaming_services",
    target: "favorite_tv_show",
    relationship: "watches_on",
  },
  {
    source: "video_games",
    target: "close_friends",
    relationship: "plays_with",
  },
  { source: "user", target: "podcast", relationship: "listens_to" },
  { source: "user", target: "tech_blog_hacker_news", relationship: "reads" },
  {
    source: "sports_teams",
    target: "event_warriors_game",
    relationship: "watching",
  },

  // Education & Learning
  { source: "college", target: "degree", relationship: "awarded" },
  { source: "degree", target: "job_title", relationship: "led_to" },
  {
    source: "current_courses",
    target: "learning_goals",
    relationship: "supports",
  },
  {
    source: "certifications",
    target: "work_skills_tech",
    relationship: "validates",
  },
  {
    source: "current_courses",
    target: "assignment_ml_project",
    relationship: "assigns",
  },
  { source: "books", target: "author_asimov", relationship: "favorite_author" },
  {
    source: "author_asimov",
    target: "current_book",
    relationship: "influences_reading",
  },

  // Financial
  { source: "salary", target: "budget", relationship: "funds" },
  { source: "budget", target: "investments", relationship: "allocates_to" },
  {
    source: "investments",
    target: "financial_goals",
    relationship: "builds_towards",
  },
  { source: "bank", target: "credit_score", relationship: "reports_to" },
  { source: "bank", target: "credit_card_chase", relationship: "issues" },
  { source: "apartment", target: "rent_amount", relationship: "costs" },
  { source: "rent_amount", target: "budget", relationship: "part_of" },

  // Housing & Travel
  { source: "current_city", target: "apartment", relationship: "location_of" },
  { source: "car", target: "commute", relationship: "used_for" },
  { source: "commute", target: "company", relationship: "to" },
  {
    source: "dream_destination",
    target: "favorite_cuisine",
    relationship: "inspired_by",
  },
  {
    source: "last_vacation",
    target: "girlfriend",
    relationship: "traveled_with",
  },
];
